import React, { useEffect, useRef, useState } from "react";
import { Radio } from "lucide-react";

import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../../components/feedback/EmptyState";
import VideoThumbnailCard from "../../../../../components/cards/thumbnails/VideoThumbnailCard";
import PopThumbnailCard from "../../../../../components/cards/pops/PopThumbnailCard";

import { searchGroupContent } from "../../services/groupContentSearchService";

import "./GroupContentSearch.css";

const LIMIT = 20;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "video", label: "Videos" },
  { value: "pop", label: "Pops" },
  { value: "stream", label: "Streams" },
];

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const SearchResultCard = ({ item, onClick }) => {
  const contentType = String(item.contentType || "").toLowerCase();

  if (contentType === "video") {
    const video = {
      ...item,
      videoId: item.contentId,
      videoTag: item.contentTag,
      thumbnailUrl: item.thumbnailUrl,
    };

    return <VideoThumbnailCard video={video} onClick={() => onClick?.(item)} />;
  }

  if (contentType === "pop") {
    const pop = {
      ...item,
      popId: item.contentId,
      popTag: item.contentTag,
      thumbnailUrl: item.thumbnailUrl,
    };

    return <PopThumbnailCard pop={pop} onClick={() => onClick?.(item)} />;
  }

  return (
    <button
      type="button"
      className="group-content-search-view__card"
      onClick={() => onClick?.(item)}
    >
      <div className="group-content-search-view__thumbnail">
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt={item.title || ""} loading="lazy" />
        ) : (
          <Radio size={26} />
        )}

        <span className="group-content-search-view__type">Stream</span>
      </div>

      <div className="group-content-search-view__details">
        <strong>{item.title || "Untitled"}</strong>
        <small>{item.status || item.username || ""}</small>
      </div>
    </button>
  );
};

export default function GroupContentSearch({
  groupTag,
  search = "",
  onSelectContent,
}) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestId = useRef(0);
  const loadingMoreRef = useRef(false);

  const normalizedSearch = String(search ?? "").trim();
  const hasSearch = Boolean(normalizedSearch);
  const isDebouncing = hasSearch && query !== normalizedSearch;

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(normalizedSearch);
    }, 350);

    return () => clearTimeout(timer);
  }, [normalizedSearch]);

  useEffect(() => {
    const currentRequest = ++requestId.current;

    setResults([]);
    setTotal(0);
    setHasMore(false);
    setError("");
    setLoadingMore(false);
    loadingMoreRef.current = false;

    if (!groupTag || !query) {
      setLoading(false);
      return;
    }

    const loadResults = async () => {
      setLoading(true);

      try {
        const response = await searchGroupContent(groupTag, {
          q: query,
          type,
          limit: LIMIT,
          offset: 0,
        });

        if (currentRequest !== requestId.current) return;

        const data = response?.data ?? {};
        const items = Array.isArray(data.results) ? data.results : [];

        const nextTotal = Number(data.total ?? 0);

        setResults(items);
        setTotal(nextTotal);

        setHasMore(
          typeof data.hasMore === "boolean"
            ? data.hasMore
            : items.length < nextTotal,
        );
      } catch (requestError) {
        if (currentRequest !== requestId.current) return;

        setError(
          getErrorMessage(requestError, "Unable to search group content."),
        );
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false);
        }
      }
    };

    void loadResults();

    return () => {
      requestId.current += 1;
    };
  }, [groupTag, query, type]);

  const handleLoadMore = async () => {
    if (
      !groupTag ||
      !query ||
      loading ||
      loadingMoreRef.current ||
      !hasMore ||
      isDebouncing
    ) {
      return;
    }

    const currentRequest = requestId.current;
    const offset = results.length;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    setError("");

    try {
      const response = await searchGroupContent(groupTag, {
        q: query,
        type,
        limit: LIMIT,
        offset,
      });

      if (currentRequest !== requestId.current) return;

      const data = response?.data ?? {};
      const items = Array.isArray(data.results) ? data.results : [];

      const nextTotal = Number(data.total ?? 0);

      setResults((previous) => {
        const existing = new Set(
          previous.map((item) => `${item.contentType}-${item.contentId}`),
        );

        const uniqueItems = items.filter(
          (item) => !existing.has(`${item.contentType}-${item.contentId}`),
        );

        return [...previous, ...uniqueItems];
      });

      setTotal(nextTotal);

      setHasMore(
        typeof data.hasMore === "boolean"
          ? data.hasMore
          : offset + items.length < nextTotal,
      );
    } catch (requestError) {
      if (currentRequest !== requestId.current) return;

      setError(getErrorMessage(requestError, "Unable to load more results."));
    } finally {
      if (currentRequest === requestId.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  };

  const handleFilterChange = (nextType) => {
    if (nextType === type) return;

    requestId.current += 1;
    loadingMoreRef.current = false;

    setType(nextType);
    setResults([]);
    setTotal(0);
    setHasMore(false);
    setLoadingMore(false);
    setError("");
  };

  const showLoading = hasSearch && (isDebouncing || loading);

  const showEmpty =
    hasSearch && !isDebouncing && !loading && !error && results.length === 0;

  const showResults =
    hasSearch && !isDebouncing && !loading && results.length > 0;

  return (
    <section className="group-content-search-view">
      <div className="group-content-search-view__filters">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`group-content-search-view__filter ${
              type === filter.value
                ? "group-content-search-view__filter--active"
                : ""
            }`}
            onClick={() => handleFilterChange(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {showLoading && (
        <ContentGrid
          items={[]}
          loading
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={8}
          showEmpty={false}
          renderItem={() => null}
        />
      )}

      {error && !showLoading && results.length === 0 && (
        <EmptyState title="Search unavailable" description={error} />
      )}

      {showEmpty && (
        <EmptyState
          title="No records found"
          description="No matching videos, pops or streams were found in this group."
        />
      )}

      {showResults && (
        <>
          <p className="group-content-search-view__count">
            {total} {total === 1 ? "result" : "results"} found
          </p>

          <ContentGrid
            items={results}
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            renderItem={(item) => (
              <SearchResultCard
                key={`${item.contentType}-${item.contentId}`}
                item={item}
                onClick={onSelectContent}
              />
            )}
          />

          {error && (
            <p className="group-content-search-view__error" role="alert">
              {error}
            </p>
          )}

          {hasMore && (
            <div className="group-content-search-view__footer">
              <button
                type="button"
                disabled={loading || loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
